import { Params } from '@angular/router';

import { URL_MODAL_PARAM_PREFIX, URL_MODAL_QUERY_PARAM } from './url-query-params';

/** The params a URL modal was opened with. Strings, because that is all a URL carries. */
export type UrlModalParams = Readonly<Record<string, string>>;

/** Params as a caller hands them over; each value is written out as a string. */
export type UrlModalParamValues = Readonly<Record<string, string | number | boolean>>;

/** The modal a URL names, with its params. */
export interface UrlModalRequest {
  readonly modalId: string;
  readonly params: UrlModalParams;
}

/** The query params of a link that opens `modalId`: `{ modal: 'kyc-survey' }`, plus `modal.<key>` for each param. */
export function modalQueryParams(modalId: string, params: UrlModalParamValues = {}): Params {
  const queryParams: Params = { [URL_MODAL_QUERY_PARAM]: modalId };

  for (const [key, value] of Object.entries(toUrlModalParams(params))) {
    queryParams[URL_MODAL_PARAM_PREFIX + key] = value;
  }

  return queryParams;
}

/** The modal `queryParams` name, or `null`. An empty or repeated `modal` names none. */
export function readUrlModalRequest(queryParams: Params): UrlModalRequest | null {
  const modalId: unknown = queryParams[URL_MODAL_QUERY_PARAM];

  if (typeof modalId !== 'string' || modalId === '') {
    return null;
  }

  const params: Record<string, string> = {};

  for (const [key, value] of Object.entries(queryParams)) {
    if (key.startsWith(URL_MODAL_PARAM_PREFIX) && typeof value === 'string') {
      params[key.slice(URL_MODAL_PARAM_PREFIX.length)] = value;
    }
  }

  return { modalId, params };
}

/** Every key the engine owns in `queryParams`, set to `null`, which `queryParamsHandling: 'merge'` removes. */
export function urlModalRemoval(queryParams: Params): Params {
  const removal: Params = {};

  for (const key of Object.keys(queryParams)) {
    if (key === URL_MODAL_QUERY_PARAM || key.startsWith(URL_MODAL_PARAM_PREFIX)) {
      removal[key] = null;
    }
  }

  return removal;
}

export function toUrlModalParams(params: UrlModalParamValues): UrlModalParams {
  return Object.fromEntries(Object.entries(params).map(([key, value]) => [key, String(value)]));
}

export function isSameUrlModalRequest(first: UrlModalRequest | null, second: UrlModalRequest | null): boolean {
  if (!first || !second) {
    return first === second;
  }

  const keys = Object.keys(first.params);

  return (
    first.modalId === second.modalId &&
    keys.length === Object.keys(second.params).length &&
    keys.every((key) => first.params[key] === second.params[key])
  );
}
